import { useMemo, useState } from "react";
import Field from "@/shared/components/field";
import Checkbox from "@/shared/components/checkbox";
import JsonSchemaEditor from "@/shared/components/json_schema_editor";

interface JsonSchemaProperty {
  type?: string | string[];
  title?: string;
  description?: string;
  default?: unknown;
  enum?: string[];
  const?: string;
}

interface ConditionalSchema {
  if?: { properties?: Record<string, JsonSchemaProperty> };
  then?: { properties?: Record<string, JsonSchemaProperty>; required?: string[] };
}

interface JsonSchema {
  type?: string;
  properties?: Record<string, JsonSchemaProperty>;
  required?: string[];
  allOf?: ConditionalSchema[];
}

interface NodePropertiesFormProps {
  schema: string;
  value: Record<string, unknown>;
  onChange: (value: Record<string, unknown>) => void;
}

export default function NodePropertiesForm({ schema, value, onChange }: NodePropertiesFormProps) {
  const parsedSchema = useMemo(() => {
    try {
      return JSON.parse(schema) as JsonSchema;
    } catch {
      return null;
    }
  }, [schema]);

  // Merges in the `then` branch of every `allOf[].if/then` block whose `if` condition (e.g.
  // operationId === "send_message") matches the node's current property values. This is how
  // an Integration node's per-operation parameters (declared as the provider manifest's
  // operation `parametersSchema`) actually surface as fields — without this, only the base
  // providerId/operationId/timeoutSeconds properties would ever render.
  const effective = useMemo(() => {
    if (!parsedSchema) return null;
    const properties: Record<string, JsonSchemaProperty> = { ...(parsedSchema.properties ?? {}) };
    const required = new Set(parsedSchema.required ?? []);

    let branchMatched = false;
    const branchKeys = new Set<string>();

    for (const branch of parsedSchema.allOf ?? []) {
      const conditions = Object.entries(branch.if?.properties ?? {});
      const matches =
        conditions.length > 0 &&
        conditions.every(([condKey, condProp]) => condProp.const !== undefined && value[condKey] === condProp.const);

      if (!matches) continue;

      branchMatched = true;
      for (const key of Object.keys(branch.then?.properties ?? {})) branchKeys.add(key);
      Object.assign(properties, branch.then?.properties ?? {});
      for (const r of branch.then?.required ?? []) required.add(r);
    }

    // When the selected operation declares its own parameters, they replace the generic
    // free-form `body` editor — showing both duplicates the same input. `body` stays only
    // if the operation's schema re-declares it explicitly.
    if (branchMatched && !branchKeys.has("body")) {
      delete properties.body;
      required.delete("body");
    }

    return { properties, required: Array.from(required) };
  }, [parsedSchema, value]);

  if (!effective?.properties || Object.keys(effective.properties).length === 0) {
    return (
      <p className="text-sm text-foreground/40">
        Este tipo de nodo no tiene propiedades configurables.
      </p>
    );
  }

  const handleChange = (key: string, val: unknown) => {
    onChange({ ...value, [key]: val });
  };

  return (
    <div className="space-y-4">
      {Object.entries(effective.properties).map(([key, prop]) => {
        // Fixed by the schema (e.g. providerId) — not user-editable, nothing to render.
        if (prop.const !== undefined) return null;

        const label = prop.title || key;
        const isRequired = effective.required.includes(key);
        const currentValue = value[key];
        const type = Array.isArray(prop.type) ? prop.type[0] : prop.type;

        if (type === "boolean") {
          return (
            <div key={key} className="py-2">
              <Checkbox
                label={label}
                checked={!!currentValue}
                onChange={(checked) => handleChange(key, checked)}
              />
            </div>
          );
        }

        if (prop.enum && prop.enum.length > 0) {
          return (
            <Field
              key={key}
              label={label}
              name={key}
              type="select"
              value={(currentValue as string) ?? ""}
              onChange={(e) => handleChange(key, e.target.value)}
              options={prop.enum.map((v) => ({ value: v, label: v }))}
              required={isRequired}
            />
          );
        }

        if (type === "number" || type === "integer") {
          return (
            <Field
              key={key}
              label={label}
              name={key}
              type="number"
              value={currentValue === undefined ? "" : (currentValue as number)}
              onChange={(e) =>
                handleChange(key, e.target.value === "" ? undefined : Number(e.target.value))
              }
              placeholder={prop.description}
              required={isRequired}
            />
          );
        }

        if (type === "string") {
          return (
            <Field
              key={key}
              label={label}
              name={key}
              type="text"
              value={(currentValue as string) ?? ""}
              onChange={(e) => handleChange(key, e.target.value)}
              placeholder={prop.description}
              required={isRequired}
            />
          );
        }

        // object / array / untyped ({}) -> raw JSON editor
        return (
          <JsonPropertyField
            key={key}
            label={label}
            name={key}
            value={currentValue}
            placeholder={prop.description}
            onChange={(val) => handleChange(key, val)}
          />
        );
      })}
    </div>
  );
}

interface JsonPropertyFieldProps {
  label: string;
  name: string;
  value: unknown;
  placeholder?: string;
  onChange: (value: unknown) => void;
}

/** JSON editor for object/array/untyped properties, keeping local text while the user types invalid JSON. */
function JsonPropertyField({ label, name, value, placeholder, onChange }: JsonPropertyFieldProps) {
  const [text, setText] = useState(() => (value === undefined ? "" : JSON.stringify(value, null, 2)));

  const handleChange = (next: string) => {
    setText(next);
    if (!next.trim()) {
      onChange(undefined);
      return;
    }
    try {
      onChange(JSON.parse(next));
    } catch {
      // Invalid JSON mid-edit: keep the raw text without propagating yet.
    }
  };

  return (
    <JsonSchemaEditor
      label={label}
      name={name}
      value={text}
      onChange={handleChange}
      placeholder={placeholder}
    />
  );
}
